import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { MessageSquare, Send, X } from 'lucide-react';
import { CreateDiscussionInput, useCommunityCategories } from '@/hooks/community/use-community-discussions';

interface PostFormProps {
  onSubmit: (data: CreateDiscussionInput) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  initialValues?: Partial<CreateDiscussionInput>;
}

const PostForm: React.FC<PostFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  initialValues = {}
}) => {
  const { data: categories = [], isLoading: categoriesLoading } = useCommunityCategories();
  
  const [formData, setFormData] = useState<CreateDiscussionInput>({
    title: initialValues.title || '',
    content: initialValues.content || '',
    category: initialValues.category || categories[0]?.name || ''
  });

  const [errors, setErrors] = useState<Partial<CreateDiscussionInput>>({});

  // Check if category is pre-selected (passed via initialValues)
  const isPreselectedCategory = Boolean(initialValues.category);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Partial<CreateDiscussionInput> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof CreateDiscussionInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <motion.form 
      onSubmit={handleSubmit}
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Title Field */}
      <div className="space-y-2">
        <Label htmlFor="title" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Post Title
        </Label>
        <Input
          id="title"
          type="text"
          placeholder="What's your post about?"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          className={`transition-all duration-200 ${
            errors.title 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
              : 'focus:border-purple-500 focus:ring-purple-500/20'
          }`}
          disabled={isSubmitting}
        />
        {errors.title && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <X size={14} />
            {errors.title}
          </p>
        )}
      </div>

      {/* Content Field */}
      <div className="space-y-2">
        <Label htmlFor="content" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Post Content
        </Label>
        <Textarea
          id="content"
          placeholder="Share your thoughts, ask questions, or start a discussion..."
          value={formData.content}
          onChange={(e) => handleInputChange('content', e.target.value)}
          className={`min-h-[120px] resize-none transition-all duration-200 ${
            errors.content 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
              : 'focus:border-purple-500 focus:ring-purple-500/20'
          }`}
          disabled={isSubmitting}
        />
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {formData.content.length}/2000 characters
          </div>
          {errors.content && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <X size={14} />
              {errors.content}
            </p>
          )}
        </div>
      </div>

      {/* Category Field - Show dropdown only if not pre-selected */}
      {!isPreselectedCategory && (
        <div className="space-y-2">
          <Label htmlFor="category" className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Category
          </Label>
          <Select
            value={formData.category}
            onValueChange={(value) => handleInputChange('category', value)}
            disabled={isSubmitting || categoriesLoading}
          >
            <SelectTrigger className={`transition-all duration-200 ${
              errors.category 
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                : 'focus:border-purple-500 focus:ring-purple-500/20'
            }`}>
              <SelectValue placeholder={categoriesLoading ? "Loading categories..." : "Select a category"} />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.name}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <X size={14} />
              {errors.category}
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-6"
        >
          Cancel
        </Button>
        
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            type="submit"
            disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
            className="px-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Publishing...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Publish Post
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </motion.form>
  );
};

export default PostForm; 